import { useTranslation } from 'react-i18next'
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis } from 'recharts'
import { type Discipline, gradesForDiscipline } from '@structural-climbing/validation'
import { useGymGradeConfig } from '../gym/useGymGradeConfig'
import { SurfaceCard } from '../../design/SurfaceCard'
import type { GradeRow } from './useMyProgress'

/**
 * Tops per grade, now against before. Moving to the right over time is what
 * getting stronger looks like, so this is the chart that shows it.
 *
 * Trimmed to the grades that have a top in either window: lead runs over 27
 * French grades and nobody climbs all of them.
 */
export function GradeComparisonChart({
  discipline,
  rows,
}: {
  discipline: Discipline
  rows: GradeRow[]
}) {
  const { t } = useTranslation()
  const gradeConfig = useGymGradeConfig()
  const scale = gradesForDiscipline(gradeConfig, discipline)

  const used = rows.filter((r) => r.current > 0 || r.previous > 0)
  if (used.length === 0) return null
  const first = used[0].grade
  const last = used[used.length - 1].grade
  const data = rows
    .filter((r) => r.grade >= first && r.grade <= last)
    .map((r) => ({
      grade: scale[r.grade - 1]?.label ?? String(r.grade),
      current: r.current,
      previous: r.previous,
    }))

  const title =
    discipline === 'lead' ? t('routes.disciplineLead') : t('routes.disciplineBouldering')
  const summary = data.map((d) => `${d.grade}: ${d.current} / ${d.previous}`).join(', ')

  return (
    <SurfaceCard className="p-5">
      <p className="text-sm font-semibold">{t('progress.gradesTitle', { discipline: title })}</p>
      <p className="mb-4 text-xs text-foreground/50">{t('progress.gradesSubtitle')}</p>
      <div role="img" aria-label={`${title}. ${summary}`} data-testid={`grades-${discipline}`}>
        <ResponsiveContainer width="100%" height={160}>
          <BarChart data={data} margin={{ top: 8, right: 4, left: 4, bottom: 0 }} barGap={2}>
            <XAxis
              dataKey="grade"
              tick={{ fontSize: 12, fill: 'hsl(var(--heroui-foreground) / 0.45)' }}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip
              cursor={{ fill: 'var(--gym-ink)', fillOpacity: 0.07 }}
              formatter={(value: number, key: string) => [
                value,
                key === 'current' ? t('progress.legendNow') : t('progress.legendBefore'),
              ]}
            />
            <Bar
              dataKey="previous"
              fill="hsl(var(--heroui-foreground) / 0.18)"
              radius={[4, 4, 0, 0]}
              maxBarSize={18}
            />
            <Bar dataKey="current" fill="var(--gym-ink)" radius={[4, 4, 0, 0]} maxBarSize={18} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </SurfaceCard>
  )
}
